/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import {
  ChoiceToggle,
  DocumentList,
  ExpProcessRow,
  PageHeader,
  PaperConfigMissing,
  StepActions,
} from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { usePostValidateNextStageMutation } from "@api/applications";
import { ApiError, BofuTabDocument, PersonaStructure } from "@interfaces/response";
import {
  applyPersonDocumentChange,
  buildPersonDocuments,
  filterPersonsForFormHub,
  findPersonasTab,
  formatPersonName,
  PersonDocumentChange,
} from "@components/PersonForm/personHelpers";
import { ALTO_SKELETON_EXPEDIENTE_PF, BOFU_TABS_CONFIG, BOFU_TABS_FORM_CODES, PATHS_EXPEDIENTE_V2, STATUS_CATALOG } from "@helpers/constantes";
import { validateDocumentStructure } from "@components/TabBofu/functions";
import { setModalErrorVisible } from "@store/slices/appSlice";
import useStatusConfig from "@hooks/useStatusConfig";
import * as Sentry from "@sentry/react";
import { UUID } from "crypto";
import { AccountBalanceOutlined, AssignmentOutlined, ShieldOutlined } from "@mui/icons-material";
import { isFormStatusEditable } from "@helpers/funciones";

/** Un documento cuenta como cargado cuando tiene archivo; los múltiples, con al menos uno. */
const tieneArchivo = (docItem: BofuTabDocument) =>
  docItem.multiple ? docItem.list_values.some(Boolean) : Boolean(docItem.file_value);

export const ExpedientePF = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(STATUS_CATALOG.avance_expediente_digital.code);
  const applicationData = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const bofuLoading = useSelector((state: RootState) => state.bofu.isLoading);

  const [persons, setPersons] = useState<PersonaStructure[]>([]);
  const [documentsByPerson, setDocumentsByPerson] = useState<Record<string, BofuTabDocument[]>>({});
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [isLoadingPersons, setIsLoadingPersons] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTabConfigMissing, setIsTabConfigMissing] = useState(false);
  const [hasIdentityValidation, setHasIdentityValidation] = useState(false);
  const [hasPersonalForm, setHasPersonalForm] = useState(false);
  const [hasBankForm, setHasBankForm] = useState(false);

  const [getPersons] = useLazyGetPersonsQuery();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const selectedPerson = useMemo(
    () => persons.find((p) => p.id === selectedPersonId) || null,
    [persons, selectedPersonId],
  );

  const selectedDocuments = selectedPersonId ? documentsByPerson[selectedPersonId] || [] : [];

  const loadData = async () => {
    if (!applicationData?.id || !applicationData?.type) {
      setIsLoadingPersons(false);
      return;
    }

    try {
      setIsLoadingPersons(true);

      const [response, tabsConfig, uploadedDocuments] = await Promise.all([
        getPersons({ loanApplicationId: applicationData.id }).unwrap(),
        getTabsConfig({
          taxcode: applicationData.type,
          code: BOFU_TABS_CONFIG.tab_personas.code,
        }).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          status: true,
        }).unwrap(),
      ]);

      const personList = filterPersonsForFormHub(response || []);
      const personasTab = findPersonasTab(tabsConfig);
      if (!personasTab) {
        Sentry.captureMessage("No se encontró la configuración del tab de personas", {
          extra: { component: "ExpedientePF-loadData", applicationId: applicationData.id },
        });
        setIsTabConfigMissing(true);
        return;
      }
      setIsTabConfigMissing(false);

      const tabDocuments = (personasTab.documents || []).filter(validateDocumentStructure);
      const forms = personasTab.forms || [];
      setHasIdentityValidation(!!forms.find((f) => f.code === BOFU_TABS_FORM_CODES.validacion_identidad));
      setHasPersonalForm(!!forms.find((f) => f.code === BOFU_TABS_FORM_CODES.formularios_persona));
      setHasBankForm(!!forms.find((f) => f.code === BOFU_TABS_FORM_CODES.informacion_bancaria));

      const nextDocuments = Object.fromEntries(
        personList.map((person) => [
          person.id,
          buildPersonDocuments({
            person,
            tabDocuments,
            uploadedDocuments: uploadedDocuments || [],
            clientDetailsId: applicationData.client_details_id as UUID,
            allPersons: personList,
          }),
        ]),
      );

      setPersons(personList);
      setDocumentsByPerson(nextDocuments);
      setSelectedPersonId((current) => {
        if (current && personList.some((p) => p.id === current)) return current;
        return personList[0]?.id || null;
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: { component: "ExpedientePF-loadData" },
      });
      setPersons([]);
      setDocumentsByPerson({});
      setSelectedPersonId(null);
    } finally {
      setIsLoadingPersons(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [applicationData?.id, applicationData?.type]);

  const handleDocumentChange = (change: PersonDocumentChange) => {
    if (!selectedPersonId) return;
    setDocumentsByPerson((current) => ({
      ...current,
      [selectedPersonId]: applyPersonDocumentChange(current[selectedPersonId] || [], change),
    }));
  };

  const handleClickIdentityValidation = () => {
    if (!selectedPersonId) return;
    navigate(`${PATHS_EXPEDIENTE_V2.validacionIdentidad.path}/${selectedPersonId}`);
  };

  const handleOpenPersonalForm = () => {
    if (!selectedPersonId) return;
    navigate(`${BOFU_TABS_CONFIG.tab_personas.url}/${selectedPersonId}`);
  };

  const handleOpenBankInfo = () => {
    if (!selectedPersonId) return;
    navigate(PATHS_EXPEDIENTE_V2.informacionBancaria.path);
  };

  const isLoading = bofuLoading || isLoadingPersons;

  const todosCompletos = useMemo(
    () =>
      persons.length > 0 &&
      Object.values(documentsByPerson).every((docs) => docs.filter((d) => d.required).every(tieneArchivo)) &&
      persons.every((person) => {
        if (hasPersonalForm && person.personal_information_status !== "COMPLETO") return false;
        if (hasBankForm && person.bank_references_status !== "COMPLETO") return false;
        if (hasIdentityValidation && person.identity_validation_completed !== true) return false;
        return true;
      }),
    [persons, documentsByPerson, hasPersonalForm, hasBankForm, hasIdentityValidation],
  );

  const handleContinue = async () => {
    if (!todosCompletos || !nextStatus) return;

    try {
      setIsSubmitting(true);
      await triggerPostNextStage({
        applicationId: applicationData.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: { component: "ExpedientePF-handleContinue" },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="EXPEDIENTE"
        title="Documentos de tu expediente"
        subtitle="Con esto tu ejecutivo prepara el contrato de arrendamiento. Puedes subirlos en varias sesiones."
      />

      {isLoading ? (
        <Skeleton variant="rectangular" height={ALTO_SKELETON_EXPEDIENTE_PF} sx={{ borderRadius: 2 }} />
      ) : isTabConfigMissing ? (
        <PaperConfigMissing />
      ) : persons.length === 0 ? (
        <Typography color="primary.dark" fontSize={16} textAlign="center" fontWeight={600} mt={3}>
          No se encontraron personas registradas
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          {persons.length > 1 && (
            <ChoiceToggle
              value={selectedPersonId ?? ""}
              onChange={(v) => setSelectedPersonId(v as string)}
              options={persons.map((p) => ({
                value: p.id,
                label: formatPersonName(p),
              }))}
            />
          )}

          {selectedPerson && (
            <Fragment>
              {hasPersonalForm && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  <ExpProcessRow
                    heading="Información personal"
                    title="Llena tu información personal"
                    hint="Datos de contacto, domicilio y situación laboral"
                    status={selectedPerson.personal_information_status ?? "PENDIENTE"}
                    icon={<AssignmentOutlined sx={{ fontSize: 18 }} />}
                    label="Llenar"
                    onClick={handleOpenPersonalForm}
                    disabled={!isFormStatusEditable(selectedPerson.personal_information_status ?? "PENDIENTE")}
                    data-testid="expediente-pf-personal-button"
                  />
                </Paper>
              )}

              {hasBankForm && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  <ExpProcessRow
                    heading="Información bancaria"
                    title="Proporciona tus datos bancarios"
                    hint="CLABE interbancaria y cuenta de depósito"
                    status={selectedPerson.bank_references_status ?? "PENDIENTE"}
                    icon={<AccountBalanceOutlined sx={{ fontSize: 18 }} />}
                    label="Llenar"
                    onClick={handleOpenBankInfo}
                    disabled={!isFormStatusEditable(selectedPerson.bank_references_status ?? "PENDIENTE")}
                    data-testid="expediente-pf-banco-button"
                  />
                </Paper>
              )}

              {hasIdentityValidation && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  <ExpProcessRow
                    heading="Verifica tu identidad"
                    hint="Selfie con prueba de vida + identificación oficial"
                    status={selectedPerson.identity_validation_completed === true ? "COMPLETO" : "PENDIENTE"}
                    icon={<ShieldOutlined sx={{ fontSize: 18 }} />}
                    label="Validar"
                    onClick={handleClickIdentityValidation}
                    disabled={
                      !isFormStatusEditable(
                        selectedPerson.identity_validation_completed === true ? "COMPLETO" : "PENDIENTE",
                      )
                    }
                    data-testid="expediente-pf-identidad-button"
                  />
                </Paper>
              )}

              {selectedDocuments.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                    <DocumentList
                      key={selectedPerson.id}
                      documents={selectedDocuments}
                      onChange={(params) => handleDocumentChange(params)}
                      onChangeMultiple={(params) => handleDocumentChange(params)}
                    />
                  </Paper>
                </Box>
              )}
            </Fragment>
          )}

          <StepActions
            onNext={handleContinue}
            nextLabel="Enviar expediente"
            nextDisabled={!todosCompletos || isSubmitting}
            nextTestId="expediente-pfae-continue-button"
          />
        </Box>
      )}
    </Box>
  );
};
